import app from 'ampersand-app'
import { useEffect, useMemo, useState } from 'preact/hooks'
import useSWR from 'swr'
import type { GetTransactionsResponse, Location } from './types'

export const api = async (
  endpoint: string,
  options: { method?: RequestInit['method']; body?: unknown } = {}
) => {
  const response = await fetch(endpoint, {
    method: options.method,
    headers: {
      'content-type': 'application/json'
    },
    body: options.body ? JSON.stringify(options.body) : undefined
  })

  if (!response.ok) {
    const errorResponse = await response.json()
    const error = new Error(errorResponse.message || response.statusText)
    // @ts-ignore
    error.statusCode = response.status
    // @ts-ignore
    error.response = errorResponse
    throw error
  }

  if (response.status !== 204) {
    return response.json()
  }
}

/**
 * @param {string} eventIdOrSlug
 * @param {{
 *   sourceId: string;
 *   targetId: string;
 *   productId: string;
 *   units: number;
 *   comment?: string;
 *   isFree?: boolean
 * }[]} transactions
 */
export function bulkCreateTransactions(
  eventIdOrSlug: string,
  transactions: {
    sourceId: string
    targetId: string
    productId: string
    units: number
    comment?: string
    isFree?: boolean
  }[]
) {
  return api(`/api/events/${eventIdOrSlug}/transactions`, {
    method: 'POST',
    body: transactions
  })
}

export function useCurrentEventSlug() {
  const [currentEventSlug, setCurrentEventSlug] = useState(
    /** @type {string} */ (app.me.currentEvent.slug)
  )

  useEffect(() => {
    const syncSlug = () => {
      setCurrentEventSlug(app.me.currentEvent.slug)
    }
    app.on('sync', syncSlug)

    return () => {
      app.off('sync', syncSlug)
    }
  }, [])

  return currentEventSlug
}

export function useCurrentRole() {
  const [currentRole, setCurrentRole] = useState<
    'admin' | 'dispatcher' | 'bar'
  >(app.me.role)

  useEffect(() => {
    const syncSlug = () => {
      setCurrentRole(app.me.role)
    }
    app.on('sync', syncSlug)

    return () => {
      app.off('sync', syncSlug)
    }
  }, [])

  return currentRole
}

export function useTransactions() {
  const eventSlug = useCurrentEventSlug()
  const qs = new URLSearchParams({ state: 'transit' })
  qs.append('state', 'accepted')

  const response = useSWR<GetTransactionsResponse>(
    `/api/events/${eventSlug}/transactions?${qs}`,
    null,
    {
      refreshInterval: 3000
    }
  )

  return response
}

export function useLocations(eventIdOrSlug: string) {
  const { data: locations, error } = useSWR<Location[]>(
    `/api/events/${eventIdOrSlug}/locations`
  )

  const { storage, bars, delivery } = useMemo(() => {
    const storage = locations?.filter(
      (location) => location.type === 'storage' || location.type === 'crash'
    )
    const bars = locations?.filter((location) => location.type === 'bar')
    const delivery = locations?.find((location) => location.type === 'delivery')

    return {
      storage,
      bars,
      delivery
    }
  }, [locations])

  return {
    error,
    locations,
    storage,
    bars,
    delivery
  }
}

/**
 * @param {string} locationId
 */
export function useLocationById(locationId: string) {
  const eventSlug = useCurrentEventSlug()
  const response = useSWR<Location>(
    `/api/events/${eventSlug}/locations/${locationId}`
  )

  return response
}
